// Alternate chord instruments (sampled piano / acoustic guitar) via Tone.Sampler.
// They reuse the EXACT voicing computed by voiceChord (drone bass + chord tones
// in the fixed register window), so only the timbre changes. Loaded lazily the first time an
// instrument is used; a Tone.Sampler needs only a sparse sample map and
// pitch-shifts to fill the gaps, so this is a couple dozen small mp3s per
// instrument, not "every note", and nothing is fetched at page load.
//
// The "Default" drone timbre stays on multiNotePlayer (with its per-degree
// mixer / EQ). These samplers are deliberately simpler: volume + a light reverb.

import * as Tone from 'tone';
import { createMixBus } from './playbackMix';
import { connectReverbSend, whenReverbReady } from './reverbBus';
import { getChordNotes, voiceChord } from './musicTheory';
import multiNotePlayer from './multiNotePlayer';
import { loadSampleBank, sampleUrls } from './sampleBanks';

// Les echantillons eux-memes (urls, baseUrl, telechargement, decodage) vivent
// dans sampleBanks : le piano du manche et le bouton Ear jouent les memes
// fichiers, et ils ne sont decodes qu'une fois pour toute la page. Ne restent
// ici que les reglages propres a l'instrument d'accords.
const SAMPLE_SETS = {
  piano: {
    // Ancien `wet` de la banque, repris tel quel en dosage direct/envoi vers
    // l'unique réverbération (cf reverbBus). La durée est désormais commune.
    reverbWet: 0.3,
    release: 1.2
  },
  guitar: {
    reverbWet: 0.25,
    release: 0.8
  }
};

// name -> { sampler, ready: Promise<sampler|null>, active: string[] }
const banks = {};

const loadBank = (name) => {
  if (banks[name]) return banks[name];
  const cfg = SAMPLE_SETS[name];
  // Les deux bus de mix de la banque (accords et bourdon). Chacun envoie vers
  // l'unique reverberation d'apres son fader, donc baisser un curseur baisse
  // aussi sa queue, comme quand la reverb etait en insert devant le bus.
  const output = createMixBus('chords');
  const droneOutput = createMixBus('drone');
  connectReverbSend(output, cfg.reverbWet);
  connectReverbSend(droneOutput, cfg.reverbWet);
  // `settled` dit que la banque a fini de se resoudre, `usable` qu'elle a de quoi
  // jouer. Les deux servent a savoir, sans attendre, si on peut declencher dans le
  // tick courant : `sampler.loaded` seul ne suffit pas, la reverb a sa propre
  // generation asynchrone et jouer avant elle donnerait un accord muet.
  const entry = { sampler: null, droneSampler: null, output, droneOutput, droneActive: [], active: [], ready: null, settled: false, usable: false };
  // Les deux samplers ne sont batis qu'une fois les buffers decodes : un Sampler
  // construit sur un ToneAudioBuffer encore vide en reecrit le `onload`, celui-la
  // meme qui fait avancer le compteur de la banque partagee (cf sampleBanks).
  // Ils restent donc nuls jusque-la, ce que tous les appelants tolerent deja.
  //
  // L'attente porte sur la banque et non sur `Tone.loaded()`, qui est global :
  // il faisait dependre le premier accord de tout ce que la page telecharge par
  // ailleurs, et un echec sans rapport suffisait a rendre la banque inutilisable.
  //
  // If the samples fail to load (offline), resolve to null so callers stay
  // silent instead of throwing.
  entry.ready = Promise.all([loadSampleBank(name).ready, whenReverbReady()])
    .then(() => {
      const urls = sampleUrls(name);
      entry.droneSampler = new Tone.Sampler({ urls, release: cfg.release }).connect(droneOutput);
      entry.sampler = new Tone.Sampler({ urls, release: cfg.release }).connect(output);
      entry.settled = true;
      entry.usable = true;
      return entry.sampler;
    })
    .catch(() => { entry.settled = true; entry.usable = false; return null; });
  banks[name] = entry;
  return entry;
};

// Which sampler bank(s) a given instrument selection needs.
const banksFor = (instrument) => {
  if (instrument === 'piano' || instrument === 'lush') return ['piano'];
  if (instrument === 'guitar') return ['guitar'];
  return [];
};

// Kick off loading ahead of first use so the bank is ready when needed.
export const warmUpInstrument = (instrument) => {
  banksFor(instrument).forEach(loadBank);
};

const releaseBank = (name) => {
  const entry = banks[name];
  if (entry && entry.droneActive.length) {
    const notes = entry.droneActive;
    entry.droneActive = [];
    entry.ready.then(s => { if (s) entry.droneSampler.triggerRelease(notes); });
  }
  if (entry && entry.active.length) {
    const notes = entry.active;
    entry.active = [];
    entry.ready.then((s) => { if (s) s.triggerRelease(notes); });
  }
};

// Le declenchement proprement dit, sans une seule attente. Tout ce qui suit doit
// tenir dans le meme tick que la nappe : sur un tap manuel l'instant vise n'est
// qu'a 20 ms (MANUAL_TRIGGER_LEAD, cf audioEngine), et le moindre await rend la
// main assez longtemps pour que React re-rende l'accord entre-temps. L'instant
// etait alors passe, le Math.max plus bas le remplacait par l'heure courante, et
// le piano tombait apres la nappe. C'est le flam entendu sur un tap manuel alors
// qu'une progression, qui vise un instant fourni 200 ms a l'avance par le
// Transport, n'a jamais eu le probleme.
const fireBank = (entry, rootNote, quality, scaleRoot, explicitNotes = null, drone = true, time = null) => {
  const sampler = entry.sampler;
  const chordNotes = explicitNotes || getChordNotes(rootNote, quality);
  const noteList = voiceChord(chordNotes, rootNote, scaleRoot, { drone });
  const keys = noteList.filter(n => quality !== 'drone' && n.role !== 'drone').map(n => n.key);
  const droneKeys = noteList.filter(n => quality === 'drone' || n.role === 'drone').map(n => n.key);
  // Le Transport planifie l'accord un peu en avance : on vise ce meme instant
  // pour rester colle a la nappe. S'il est deja passe (attente des samples), on
  // retombe sur l'instant courant, ce que fait Tone par defaut.
  // Le garde-fou se compare a l'horloge reelle, jamais a Tone.now() : celui-ci
  // ajoute le lookAhead du contexte (0.2s ici), toujours superieur a l'avance du
  // Transport, donc il ecrasait systematiquement l'instant vise et le piano
  // tombait 20 a 90 ms apres la nappe et apres le clic du metronome.
  // Meme horloge dans les deux branches. Au declenchement manuel (time null) la
  // nappe part a audioContext.currentTime : viser Tone.now() ici ajoutait le
  // lookAhead (0.2s, cf synthPlayer) et le piano tombait un cinquieme de seconde
  // apres elle, alors que sur une progression les deux visaient deja le meme
  // instant fourni par le Transport.
  const when = time !== null ? Math.max(time, Tone.getContext().currentTime) : Tone.getContext().currentTime;
  // Relacher AVANT d'attaquer, et de facon synchrone. Passer par entry.ready
  // repoussait le release en microtache, donc APRES le triggerAttack : comme
  // Tone.Sampler relache toutes les sources actives d'une note, une note
  // commune aux deux accords voyait sa source fraichement declenchee coupee
  // dans la foulee, et l'accord arrivait ampute.
  if (entry.active.length) {
    sampler.triggerRelease(entry.active, when);
    entry.active = [];
  }
  if (entry.droneActive.length) entry.droneSampler.triggerRelease(entry.droneActive, when);
  if (keys.length) sampler.triggerAttack(keys, when);
  if (droneKeys.length) entry.droneSampler.triggerAttack(droneKeys, when);
  entry.droneActive = droneKeys;
  entry.active = keys;
};

// Chemin lent : la banque n'est pas encore prete, on l'attend. Ne sert qu'aux
// tout premiers accords de la session, et a la reprise apres une interruption.
const playBank = async (name, rootNote, quality, scaleRoot, explicitNotes = null, drone = true, time = null) => {
  const entry = loadBank(name);
  const sampler = await entry.ready;
  if (!sampler) return; // samples unavailable; stay silent
  fireBank(entry, rootNote, quality, scaleRoot, explicitNotes, drone, time);
};

// Play a chord through the sampler bank(s) the instrument needs. 'lush' plays the
// piano sampler here while the caller (audioEngine) layers the default WAV engine
// on top. 'default' needs no sampler.
// Vrai quand le sampler peut partir dans le tick courant, aux memes conditions que
// la nappe. Les trois comptent : le contexte doit tourner, la nappe doit avoir eu
// son geste de deverrouillage (le garde de session le remet a faux apres une
// interruption iOS, et elle repasse alors par un await), et les banques doivent
// etre chargees, reverb comprise. Si l'une manque, les deux couches reprennent
// ensemble le chemin asynchrone : elles attendent toutes les deux, donc elles
// restent collees. Ce qu'il faut eviter est qu'une seule des deux attende.
const canFireNow = (names) => (
  Tone.getContext().state === 'running'
  && multiNotePlayer.gestureUnlocked
  && names.every((name) => banks[name] && banks[name].settled && banks[name].usable)
);

export const playChordInstrument = (instrument, rootNote, quality, scaleRoot, explicitNotes = null, drone = true, time = null) => {
  const names = banksFor(instrument);
  if (names.length === 0) return Promise.resolve();
  if (canFireNow(names)) {
    names.forEach((name) => fireBank(banks[name], rootNote, quality, scaleRoot, explicitNotes, drone, time));
    return Promise.resolve();
  }
  // Ensure the Tone context is running (called from a user gesture); without this
  // the sampler can stay silent if no Tone audio has played yet this session.
  return Tone.start()
    .catch(() => {})
    .then(() => Promise.all(names.map((name) => playBank(name, rootNote, quality, scaleRoot, explicitNotes, drone, time))))
    .then(() => undefined);
};

// Release every sampler's currently sounding notes.
export const stopAllInstruments = () => {
  Object.keys(banks).forEach(releaseBank);
};

// Joue une note isolee sur la banque guitare (meme sampler que l'instrument
// "guitar", hors logique de voicing d'accord). Utilise par /lick pour faire
// entendre les notes d'un lick pendant la lecture. Synchrone a dessein : le
// caller passe un `time` calcule a l'instant (pre-scheduling), donc on declenche
// immediatement au lieu d'attendre une promesse qui rendrait ce time perime.
// Tant que les samples ne sont pas charges (sampler.loaded), on saute la note
// (sinon Tone jette "buffer not loaded") : warmUpInstrument est appele en amont
// pour que ce cas ne dure que les toutes premieres millisecondes.
// Retourne true si la note a ete declenchee, false si le sampler n'est pas
// encore charge (le caller peut alors reessayer plutot que de perdre la note).
export const playGuitarNote = (note, duration, time) => {
  const entry = loadBank('guitar');
  const sampler = entry.sampler;
  if (!sampler || !sampler.loaded) return false;
  sampler.triggerAttackRelease(note, duration, time);
  return true;
};

// Joue une note guitare avec un bend (courbe de pitch), sur la meme banque et la
// meme chaine de sortie (le bus des accords, avec son envoi de reverb) que
// playGuitarNote. La lecture passe par une source bufferisee dediee (le Sampler
// ne donne pas la main sur le pitch d'une note declenchee) : on reprend le
// buffer du sample le plus proche, et on automatise le playbackRate
// (AudioParam) en rampes lineaires pour suivre le bend. bendCurve = liste de
// points { at, cents } DEJA a l'echelle temporelle finale (le caller a divise par la
// vitesse) : at en secondes depuis l'attaque, cents en centiemes de demi-ton. Le
// pitch d'attaque vient du 1er point (pas de transposition du noteName). Meme
// contrat que playGuitarNote : retourne false si les samples ne sont pas encore
// charges, true sinon.
export const playGuitarBendNote = (note, durationSec, when, bendCurve) => {
  const entry = loadBank('guitar');
  const sampler = entry.sampler;
  const buffers = sampler && sampler._buffers;
  if (!sampler || !sampler.loaded || !buffers) return false;

  // Sample disponible le plus proche, comme le fait le Sampler en interne.
  const midi = Tone.Frequency(note).toMidi();
  let difference = null;
  for (let interval = 0; interval < 96; interval++) {
    if (buffers.has(midi + interval)) { difference = -interval; break; }
    if (buffers.has(midi - interval)) { difference = interval; break; }
  }
  if (difference === null) return false;
  const buffer = buffers.get(midi - difference).get();
  if (!buffer) return false;
  const playbackRate = Tone.intervalToFrequencyRatio(difference);

  const ctx = Tone.getContext();
  const start = Math.max(when, ctx.currentTime);
  const release = SAMPLE_SETS.guitar.release;
  const ATTACK = 0.006;
  const dur = Math.max(durationSec, 0.05);
  const end = start + dur;

  const src = ctx.createBufferSource();
  src.buffer = buffer;

  // Bend via playbackRate : le contexte de Tone (standardized-audio-context)
  // n'expose pas detune sur les buffer sources. Ratio du sample (transposition
  // vers la note visee) compose avec le ratio du bend en cents. 1er point pose
  // a l'attaque, puis une rampe lineaire par point ; la derniere valeur est
  // tenue jusqu'au stop (le hold du tie n'ajoute pas de point).
  const curve = bendCurve && bendCurve.length ? bendCurve : [{ at: 0, cents: 0 }];
  const rateAt = (cents) => playbackRate * Tone.intervalToFrequencyRatio(cents / 100);
  src.playbackRate.setValueAtTime(rateAt(curve[0].cents), start);
  for (let i = 0; i < curve.length; i++) {
    src.playbackRate.linearRampToValueAtTime(rateAt(curve[i].cents), start + Math.max(0, curve[i].at));
  }

  // Enveloppe de gain : courtes attaque et release pour eviter les clics.
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(1, start + Math.min(ATTACK, dur));
  gain.gain.setValueAtTime(1, end);
  gain.gain.linearRampToValueAtTime(0, end + release);

  src.connect(gain);
  Tone.connect(gain, entry.output);
  src.start(start);
  src.stop(end + release + 0.02);
  src.onended = () => { try { src.disconnect(); gain.disconnect(); } catch (_) {} };
  return true;
};
